import React, { useEffect, useRef, useState } from "react";
import { Music2, VolumeX, Volume1, Volume2 } from "lucide-react";
import { onVideoActivity } from "../lib/mediaBus";

const LS_KEY = "uw_music_enabled";
const LS_VOL = "uw_music_volume";

// Loops royalty-free jazz in the background. Auto-starts on the visitor's
// first interaction (browsers block sound before a gesture), pauses while a
// video plays, then resumes. Floating button opens a mute + volume slider.
export default function BackgroundMusic() {
  const audioRef = useRef(null);
  const wrapRef = useRef(null);

  const [enabled, setEnabled] = useState(() => {
    const v = localStorage.getItem(LS_KEY);
    return v === null ? true : v === "1";
  });
  const [volume, setVolume] = useState(() => {
    const v = parseFloat(localStorage.getItem(LS_VOL));
    return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0.35;
  });
  const [videoActive, setVideoActive] = useState(false);
  const [open, setOpen] = useState(false);

  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  const videoActiveRef = useRef(false);
  const interactedRef = useRef(false);

  const tryPlay = () => {
    const a = audioRef.current;
    if (a && enabledRef.current && !videoActiveRef.current) {
      a.play().catch(() => {});
    }
  };

  // Apply volume to the audio element.
  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  // Choice 2b: start on the very first tap/click/keypress anywhere.
  useEffect(() => {
    const onFirst = () => {
      if (interactedRef.current) return;
      interactedRef.current = true;
      tryPlay();
      remove();
    };
    const evts = ["pointerdown", "touchstart", "keydown", "click"];
    const remove = () =>
      evts.forEach((e) => window.removeEventListener(e, onFirst));
    evts.forEach((e) => window.addEventListener(e, onFirst, { passive: true }));
    return remove;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pause while a video plays, resume when it stops.
  useEffect(() => {
    return onVideoActivity((active) => {
      videoActiveRef.current = active;
      setVideoActive(active);
      const a = audioRef.current;
      if (!a) return;
      if (active) a.pause();
      else tryPlay();
    });
  }, []);

  // Close the slider popover when clicking outside.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener("pointerdown", onDown);
    return () => window.removeEventListener("pointerdown", onDown);
  }, [open]);

  const toggleMute = () => {
    const nv = !enabled;
    setEnabled(nv);
    enabledRef.current = nv;
    interactedRef.current = true;
    localStorage.setItem(LS_KEY, nv ? "1" : "0");
    const a = audioRef.current;
    if (!a) return;
    if (nv && !videoActiveRef.current) a.play().catch(() => {});
    else a.pause();
  };

  const onVolume = (e) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    localStorage.setItem(LS_VOL, String(v));
    // Nudging the slider up while muted turns the music back on.
    if (v > 0 && !enabledRef.current) {
      setEnabled(true);
      enabledRef.current = true;
      localStorage.setItem(LS_KEY, "1");
      interactedRef.current = true;
      tryPlay();
    }
  };

  const isOn = enabled && !videoActive;
  const VolIcon = !enabled ? VolumeX : volume < 0.5 ? Volume1 : Volume2;

  return (
    <div ref={wrapRef} className="fixed bottom-6 left-6 z-[95]">
      <audio
        ref={audioRef}
        src={`${process.env.PUBLIC_URL || ""}/audio/jazz-bg.mp3`}
        loop
        preload="auto"
      />

      {/* Volume slider popover */}
      <div
        data-testid="music-volume-panel"
        className={`absolute bottom-14 left-0 origin-bottom-left transition-all duration-200 ${
          open
            ? "opacity-100 translate-y-0 pointer-events-auto"
            : "opacity-0 translate-y-2 pointer-events-none"
        }`}
      >
        <div className="flex items-center gap-3 bg-[#16110E] border border-[#F0C766]/40 rounded-full pl-3 pr-4 py-2.5 shadow-[0_12px_30px_-10px_rgba(0,0,0,0.8)] whitespace-nowrap">
          <button
            data-testid="music-mute-toggle"
            onClick={toggleMute}
            aria-label={enabled ? "Mute music" : "Play music"}
            className="text-[#F0C766] hover:text-[#F3EAD7] transition-colors shrink-0"
          >
            <VolIcon size={18} />
          </button>
          <input
            data-testid="music-volume-slider"
            type="range"
            min="0"
            max="1"
            step="0.01"
            value={enabled ? volume : 0}
            onChange={onVolume}
            aria-label="Music volume"
            className="uw-volume w-28 md:w-32"
          />
        </div>
      </div>

      {/* Main floating button */}
      <button
        data-testid="music-toggle"
        onClick={() => setOpen((o) => !o)}
        aria-label="Background music controls"
        aria-expanded={open}
        title="Background music"
        className={`relative w-12 h-12 rounded-full flex items-center justify-center border transition-all duration-300 shadow-[0_10px_28px_-8px_rgba(0,0,0,0.7)] ${
          enabled
            ? "bg-[#F0C766] text-[#16110E] border-[#F0C766] hover:-translate-y-0.5"
            : "bg-[#16110E] text-[#F0C766] border-[#F0C766]/40 hover:bg-[#241E19]"
        }`}
      >
        {isOn && (
          <span className="absolute inset-0 rounded-full border border-[#F0C766] animate-ping opacity-40" />
        )}
        {enabled ? <Music2 size={20} /> : <VolumeX size={20} />}
      </button>
    </div>
  );
}
